import { useEffect, useMemo, useState } from 'react'

import { useApiClient } from '@/api/ApiClientProvider'
import {
  MAX_CUSTOMERS_PAGE,
  type CustomerRow,
  type CustomerSearch,
  type CustomerSort,
  type CustomersList,
  type CustomersQuery,
  type SortOrder,
} from '@/api/customers'
import type { Envelope } from '@/api/types'

import { foldPages } from '../list/paging'
import { identify, type CustomersResult } from './customersList'
import { sortCustomers } from './sorting'

/*
 * The search: one of the two requests that identify a person, so never cached. A plain
 * request held in the page's own state, so what was searched for lives only as long as
 * the page does: never in a URL, a query key, the query cache, the browser's storage or
 * a log. It is never retried or refetched on its own either, because every search is a
 * new record in the audit log.
 */

/** One answer of the search route, as the folded list the table reads. */
export function searchResult(answer: Envelope<CustomersList>): CustomersResult {
  // One page always folds.
  return foldPages<CustomerRow, CustomersList>([answer], identify)!
}

export type SearchState =
  | { status: 'idle' }
  | { status: 'loading'; search: CustomerSearch }
  | { status: 'done'; search: CustomerSearch; result: CustomersResult }
  | { status: 'failed'; search: CustomerSearch; error: unknown }

/**
 * The search: what it looks for, and its answer within the list's `scope` (who is
 * listed, the filters, the period), in the list's `sort`. Run again only when the scope
 * changes, since the basis still applies to a search; a new order sorts the found
 * customers here instead, so it is never another search, nor another audit record.
 * Cleared by `clear`. An answer that arrives after a newer search started is dropped.
 */
export function useCustomerSearch(scope: CustomersQuery, sort: { sort: CustomerSort; order: SortOrder }) {
  const client = useApiClient()
  const [search, setSearch] = useState<CustomerSearch | null>(null)
  const [state, setState] = useState<SearchState>({ status: 'idle' })

  useEffect(() => {
    if (!search) {
      setState({ status: 'idle' })
      return
    }
    let current = true
    setState({ status: 'loading', search })
    client.searchCustomers({ ...scope, limit: MAX_CUSTOMERS_PAGE, offset: 0 }, search).then(
      (answer) => {
        if (current) setState({ status: 'done', search, result: searchResult(answer) })
      },
      (error: unknown) => {
        if (current) setState({ status: 'failed', search, error })
      },
    )
    return () => {
      current = false
    }
  }, [client, scope, search])

  const sorted = useMemo((): SearchState => {
    if (state.status !== 'done') return state
    // The same row objects in a new order, so each keeps its key.
    const rows = sortCustomers(state.result.rows, (row) => row, sort.sort, sort.order)
    return { ...state, result: { ...state.result, rows } }
  }, [state, sort.sort, sort.order])

  return { state: sorted, run: setSearch, clear: () => setSearch(null) }
}
